import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { InputLabel } from '../InputLabel/InputLabel.js';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage.js';
import { HelpText } from '../HelpText/HelpText.js';

var _excluded = ["className", "children", "defaultOption", "errormessage", "forwardRef", "helptext", "hideDefaultOption", "id", "isOptional", "isValid", "label", "name", "onBlur", "onChange", "onFocus", "options", "required", "tooltipText", "value"],
  _excluded2 = ["label", "groupOptions"],
  _excluded3 = ["label"],
  _excluded4 = ["label", "value"];
/**
 * Dropdown
 */
var DropdownComponent = props => {
  // Extract known attributes from props into variables
  var {
      className,
      // Capture the classname so it cannot be applied to the textfield
      children,
      defaultOption,
      errormessage,
      forwardRef,
      helptext,
      hideDefaultOption,
      id,
      isOptional,
      isValid,
      label,
      name,
      onBlur,
      onChange,
      onFocus,
      options,
      required,
      tooltipText,
      value
    } = props,
    attributes = _objectWithoutProperties(props, _excluded);
  attributes.id = id;
  attributes.className = 'form-control';
  attributes.name = name ? name : id; // eslint-disable-line

  var labelProps = {
    id,
    isOptional,
    label,
    tooltipText
  };
  if (typeof onBlur === 'function') {
    attributes.onBlur = onBlur;
  }
  if (typeof onChange === 'function') {
    attributes.value = value;
    attributes.onChange = e => {
      // @ts-ignore
      onChange(attributes.name, e.target.value, e);
    };
  } else {
    attributes.defaultValue = value;
  }
  if (typeof onFocus === 'function') {
    attributes.onFocus = onFocus;
  }
  if (typeof required === 'boolean' && required === true) {
    attributes['aria-required'] = true;
  }
  var ErrorMessageComponent;
  var HelpTextComponent;
  if (typeof isValid === 'boolean' && isValid === false) {
    // Invalid
    attributes.className += ' is-invalid';
    attributes['aria-invalid'] = true;

    // Invalid with error message
    if (errormessage) {
      attributes['aria-describedby'] = "".concat(id, "-errormessage");
      ErrorMessageComponent = /*#__PURE__*/React.createElement(ErrorMessage, {
        id: id,
        message: errormessage
      });
    }
  } else if (helptext) {
    attributes['aria-describedby'] = "".concat(id, "-helptext");
    HelpTextComponent = /*#__PURE__*/React.createElement(HelpText, {
      id: id
    }, helptext);
  }
  // eslint-disable-next-line
  var renderDefaultOption = () => {
    if (!hideDefaultOption || isOptional) {
      return /*#__PURE__*/React.createElement("option", {
        value: ""
      }, defaultOption);
    }
  };
  var hcid = "".concat(DropdownComponent.name.toLocaleLowerCase(), "-").concat("10.6.2");
  return /*#__PURE__*/React.createElement("div", {
    className: "form-group",
    "data-hcid": hcid
  }, /*#__PURE__*/React.createElement(InputLabel, labelProps), /*#__PURE__*/React.createElement("select", _extends({}, attributes, {
    ref: forwardRef
  }), renderDefaultOption(), options && options.map((option, i) => {
    // eslint-disable-next-line
    if (option.hasOwnProperty('groupOptions')) {
      var _ref = option,
        {
          label: optionGroupLabel,
          groupOptions
        } = _ref,
        optionGroupAttributes = _objectWithoutProperties(_ref, _excluded2);
      return (
        /*#__PURE__*/
        /* eslint-disable react/no-array-index-key */
        React.createElement("optgroup", _extends({
          key: "group-".concat(i),
          label: optionGroupLabel
        }, optionGroupAttributes), groupOptions && groupOptions.map((opt, index) => {
          var {
              label: optLabel
            } = opt,
            optAttributes = _objectWithoutProperties(opt, _excluded3);
          return /*#__PURE__*/React.createElement("option", _extends({
            key: "dropdown-".concat(i, "-").concat(index)
          }, optAttributes), optLabel);
        }))
      );
    }
    var _ref2 = option,
      {
        label: optionLabel,
        value: optionValue
      } = _ref2,
      optionAttributes = _objectWithoutProperties(_ref2, _excluded4);
    return /*#__PURE__*/React.createElement("option", _extends({
      key: "dropdown-".concat(optionValue),
      value: optionValue
    }, optionAttributes), optionLabel);
  })), HelpTextComponent, ErrorMessageComponent);
};
DropdownComponent.defaultProps = {
  defaultOption: 'Select',
  hideDefaultOption: false,
  isValid: true
};
DropdownComponent.displayName = 'Dropdown';
DropdownComponent.propTypes = {
  defaultOption: PropTypes.string,
  errormessage: PropTypes.string,
  helptext: PropTypes.string,
  hideDefaultOption: PropTypes.bool,
  id: PropTypes.string.isRequired,
  isOptional: PropTypes.bool,
  isValid: PropTypes.bool,
  label: PropTypes.string.isRequired,
  /* Name will default to ID if not provided. ID is required for linking label with input */
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({
    disabled: PropTypes.bool,
    groupOptions: PropTypes.arrayOf(PropTypes.shape({
      disabled: PropTypes.bool,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })),
    label: PropTypes.string.isRequired
  })).isRequired, PropTypes.arrayOf(PropTypes.shape({
    disabled: PropTypes.bool,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })).isRequired]),
  required: PropTypes.bool,
  tooltipText: PropTypes.string,
  value: PropTypes.string.isRequired
};
var Dropdown = /*#__PURE__*/React.forwardRef((props, ref) => /*#__PURE__*/React.createElement(DropdownComponent, _extends({}, props, {
  forwardRef: ref
})));
Dropdown.displayName = 'Dropdown';

export { Dropdown, DropdownComponent };
